import { ADVISORS_BASE } from '@src/components/sco/Advisors/constants'
import { SEAT_CATEGORIES } from '@src/components/sco/Advisors/data/seatCategories'
import { SEAT_COMPENSATION_TYPES, SEAT_COMMITMENTS } from '@src/components/sco/Advisors/data/seatIndustries'
import {
  displayWhyTextWithoutCategoryRole,
  seatAsksFromWhyText,
} from '@src/components/sco/Advisors/utils/engagementMappers'
import type { ScoUserMatch } from '@src/graphql-types'

export type OpenSeatMatch = ScoUserMatch & {
  userMatchId?: string | null
}

/**
 * Advisor open-seat matches: UserMatch rows whose UserSearch is linked to an advisor
 * GroupRequest. Matches are generated pre-approval (L21), so include pending_approval
 * as well as published — exclude draft/rejected only.
 */
export const isPublishedAdvisorOpenSeat = (match: OpenSeatMatch | null | undefined): boolean => {
  const gr = match?.userSearch?.groupRequest
  if (!gr?.groupRequestId) {
    return false
  }
  if (gr.groupRequestTypeKey && gr.groupRequestTypeKey !== 'advisor') {
    return false
  }
  if (gr.publishStatusKey === 'draft' || gr.publishStatusKey === 'rejected') {
    return false
  }
  return true
}

export const filterOpenSeatMatches = (
  items: Array<OpenSeatMatch | null | undefined> | null | undefined
): OpenSeatMatch[] => {
  return (items || []).filter((m): m is OpenSeatMatch => !!m?.userMatchId && isPublishedAdvisorOpenSeat(m))
}

/** @param base Advisors product base (`/advisors` or `/requests/manage/:id/advisors`). */
export const openSeatProfileHref = (userMatchId: string, base: string = ADVISORS_BASE) =>
  `${base}/opportunities/${encodeURIComponent(userMatchId)}`

/**
 * Founder on the reverse match path:
 * GroupRequest.requestingUser (seat owner) → UserMatch.searchingUser (same person for advisor seats).
 */
export const founderFromMatch = (match: OpenSeatMatch) => {
  const gr = match.userSearch?.groupRequest
  return gr?.requestingUser || match.searchingUser || null
}

const whyRawFromMatch = (match: OpenSeatMatch): string | null =>
  match.userSearch?.groupRequest?.needExplanation || match.userSearch?.naturalLanguagePrompt || null

/** Selected seat asks from stored why text; omits the canned category role. */
export const asksFromMatch = (match: OpenSeatMatch): string[] => seatAsksFromWhyText(whyRawFromMatch(match))

/**
 * Company · title when available; otherwise a short default for list cards.
 * Seat asks live in `asksFromMatch` / `whyTextFromMatch`, not here.
 */
export const founderSubtitleFromMatch = (match: OpenSeatMatch): string => {
  const founder = founderFromMatch(match)
  const company = companyFromMatch(match)
  return (
    [company?.name, founder?.title || founder?.shortBio].filter(Boolean).join(' · ') ||
    'Founder recruiting advisors'
  )
}

export const companyFromMatch = (match: OpenSeatMatch) =>
  match.userSearch?.groupRequest?.requestingCompany || null

export const seatTitleFromMatch = (match: OpenSeatMatch) =>
  match.userSearch?.groupRequest?.title || match.userSearch?.title || 'Open board seat'

export const whyTextFromMatch = (match: OpenSeatMatch) =>
  displayWhyTextWithoutCategoryRole(whyRawFromMatch(match))

export const skillTitlesFromMatch = (match: OpenSeatMatch): string[] => {
  const fromRequest = match.userSearch?.groupRequest?.skillTagTitles?.filter(Boolean) as string[] | undefined
  if (fromRequest?.length) {
    return fromRequest
  }

  const required = match.userSearch?.requiredSkills || []
  // requiredSkills is [[title, relevancy, skillId], ...]
  return required
    .map(row => (Array.isArray(row) ? row[0] : null))
    .filter((t): t is string => typeof t === 'string' && t.length > 0)
}

export const matchFactorTitles = (match: OpenSeatMatch, limit = 4): string[] => {
  const factors = match.matchFactors || []
  // matchFactors: [[points, type, title, identifier], ...]
  return factors
    .map(row => (Array.isArray(row) ? String(row[2] || '') : ''))
    .filter(Boolean)
    .slice(0, limit)
}

export const matchPercentLabel = (match: OpenSeatMatch): string | null => {
  const pct = match.matchScoreAsPercent
  if (pct == null || Number.isNaN(Number(pct))) {
    return null
  }
  return `${Math.round(Number(pct))}% match`
}

/** Infer category key from seat title / factor labels for palette. */
export const categoryMetaFromMatch = (match: OpenSeatMatch) => {
  const title = (seatTitleFromMatch(match) || '').toLowerCase()
  const factors = matchFactorTitles(match, 10).join(' ').toLowerCase()
  const haystack = `${title} ${factors}`

  for (const cat of SEAT_CATEGORIES) {
    if (haystack.includes(cat.key.replace(/-/g, ' ')) || haystack.includes(cat.name.toLowerCase())) {
      return { key: cat.key, name: cat.name, palette: cat.palette, icon: cat.icon }
    }
  }
  return { key: null, name: 'Board seat', palette: 'purple', icon: SEAT_CATEGORIES[0]?.icon }
}

export const compensationLabelsFromMatch = (match: OpenSeatMatch): string[] => {
  const typed = match.userSearch?.groupRequest?.compensationTypes
  if (typed?.length) {
    return typed.map(t => t?.title || t?.key).filter(Boolean) as string[]
  }
  const keys =
    match.userSearch?.groupRequest?.compensationTypeKeys || match.userSearch?.compensationTypeKeys || []
  return (keys as string[])
    .filter(Boolean)
    .map(k => SEAT_COMPENSATION_TYPES.find(c => c.key === k)?.label || k)
}

export const commitmentLabelsFromMatch = (match: OpenSeatMatch): string[] => {
  const typed = match.userSearch?.groupRequest?.timeCommitmentTypes
  if (typed?.length) {
    return typed.map(t => t?.title || t?.key).filter(Boolean) as string[]
  }
  const keys =
    match.userSearch?.groupRequest?.timeCommitmentTypeKeys || match.userSearch?.commitmentTypeKeys || []
  return (keys as string[]).filter(Boolean).map(k => SEAT_COMMITMENTS.find(c => c.key === k)?.label || k)
}

export const founderDisplayName = (match: OpenSeatMatch): string => {
  const f = founderFromMatch(match)
  return f?.fullName || f?.displayName || f?.firstName || 'Founder'
}

export const founderProfileHref = (match: OpenSeatMatch): string | null => {
  const f = founderFromMatch(match)
  if (f?.slug) {
    return `/members/profile/${f.slug}`
  }
  return null
}

export type SeatApplicationState = 'none' | 'pending' | 'accepted' | 'declined'

export const seatApplicationState = (match: OpenSeatMatch): SeatApplicationState => {
  const commitment = match.userSearch?.groupRequest?.currentUserGroupCommitment
  if (!commitment?.groupCommitmentId) {
    return 'none'
  }
  if (commitment.rejectedAt) {
    return 'declined'
  }
  if (commitment.acceptedAt) {
    return 'accepted'
  }
  return 'pending'
}

export const hasAppliedToSeat = (match: OpenSeatMatch): boolean => seatApplicationState(match) !== 'none'
